import React, { useContext, useEffect } from "react";
import { LoadingIcon, TopDropdown } from "../../../components/index.js";
import { UserContext } from "../../../context/UserContext.jsx";

/** @typedef {import("./harvestOptimizer.types.d.ts").DropdownsProps} DropdownsProps */

/** @param {DropdownsProps} props */
const Dropdowns = ({
  siteMetadata,
  setSiteMetadata,
  currentOrg,
  setCurrentOrg,
  orgData,
  setOrgData,
  orgLoading,
  setOrgLoading,
  currentSite,
  setCurrentSite,
  siteData,
  setSiteData,
  siteLoading,
  setSiteLoading,
  currentPlot,
  setCurrentPlot,
  currentCrop,
  setCurrentCrop,
  cropData,
  setCropData,
  cropLoading,
  setCropLoading,
  settingDefaults,
  setSettingDefaults,
  searchParams,
  setSearchParams,
  greenHouseLabel,
  setGreenHouseLabel,
  blockLabel,
  setBlockLabel,
}) => {
  const { userData } = useContext(UserContext);

  useEffect(() => {
    if (currentSite && currentOrg && currentCrop) {
      const siteMetaData =
        /** @type {any} */ (currentSite).metadata?.[
          /** @type {any} */ (currentCrop).name
        ] || {};
      setSiteMetadata(siteMetaData);

      if (siteMetaData?.greenhouse_name) {
        setGreenHouseLabel(siteMetaData.greenhouse_name);
      }
      if (siteMetaData?.block_name) {
        setBlockLabel(siteMetaData.block_name);
      }
    }
  }, [
    currentSite,
    currentOrg,
    currentCrop,
    setSiteMetadata,
    setGreenHouseLabel,
    setBlockLabel,
  ]);

  return (
    <>
      {orgLoading || siteLoading ? (
        <LoadingIcon height="100px" />
      ) : (
        <div className="w-full flex flex-wrap gap-4 justify-between sm:flex-col mb-10 dropdowns sm:mb-4 !font-MuseoSans">
          {/* Org Dropdown (ID-based) */}
          {userData?.is_superuser && orgData && (
            <TopDropdown
              label="Select Org:"
              val={currentOrg ?? undefined}
              classString="flex-1 min-w-[150px] sm:mb-2"
              placeholder="Select Organization"
              changeCallback={setCurrentOrg}
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label ?? "")
                  .toLowerCase()
                  .localeCompare((optionB?.label ?? "").toLowerCase())
              }
              notFound={orgLoading ? <LoadingIcon /> : null}
              data={orgData.map((org) => ({
                value: org.id,
                label: org.name,
              }))}
            />
          )}

          {/* Site Dropdown (ID-based) */}
          <TopDropdown
            label="Select Site:"
            val={currentSite?.id ?? undefined}
            classString="flex-1 min-w-[150px] sm:mb-2"
            placeholder="Select Site"
            changeCallback={(siteId) => {
              const siteObj = siteData.find((s) => s.id === siteId);
              setCurrentSite(siteObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            notFound={siteLoading ? <LoadingIcon /> : null}
            data={siteData?.map((site) => ({
              value: site.id,
              label: site.displayName,
            }))}
          />

          {/* Crop Dropdown (ID-based) */}
          <TopDropdown
            label="Select Crop:"
            val={currentCrop?.id ?? undefined}
            placeholder="Select Crop"
            classString="flex-1 min-w-[150px] sm:mb-2"
            changeCallback={(cropId) => {
              const cropObj = cropData.find((c) => c.id === cropId);
              setCurrentCrop(cropObj);
            }}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            filterSort={(optionA, optionB) =>
              (optionA?.label ?? "")
                .toLowerCase()
                .localeCompare((optionB?.label ?? "").toLowerCase())
            }
            data={cropData?.map((crop) => ({
              value: crop.id,
              label: crop.displayName,
            }))}
          />
        </div>
      )}
    </>
  );
};

export default Dropdowns;
